import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import {
    Alert,
    AlertDescription,
    AlertTitle,
    Box,
    Button,
    Center,
    HStack,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { fetchPSCLocations, reorderPSCLocations } from '../../api';
import AccessDenied from '../../components/AccessDenied';
import Loading from '../../components/Loading/Loading';
import TitleHeader from '../../components/TitleHeader';
import { hasPermission, useTableColors } from '../../Utils';
import CreatePSCLocationModal from './CreatePSCLocationModal';
import DeletePSCModal from './DeletePSCModal';
import EditPSCLocationModal from './EditPSCLocationModal';
import ExportPSCModal from './ExportPSCModal';

const PSCLocationsPage = () => {
    const [pscLocations, setPSCLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 40;
    const [selectedItem, setSelectedItem] = useState(null);
    const [isOpenCreate, setIsOpenCreate] = useState(false);
    const [isOpenEdit, setIsOpenEdit] = useState(false);
    const [isOpenDelete, setIsOpenDelete] = useState(false);
    const [exportModal, setExportModal] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    const coolors = useTableColors();

    useEffect(() => {
        fetchLocations();
    }, []);

    const fetchLocations = async (page = 1) => {
        setLoading(true);
        try {
            const result = await fetchPSCLocations(page, itemsPerPage, false);
            if (result.success) {
                setPSCLocations(result.data.locations);
                setCurrentPage(result.data.currentPage);
                setTotalPages(result.data.totalPages);
            } else {
                setError(result.error || 'Failed to fetch locations');
            }
        } catch (error) {
            setError('Error fetching locations');
        } finally {
            setLoading(false);
        }
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        const items = Array.from(pscLocations);
        const [reordered] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reordered);
        setPSCLocations(items);
        setHasChanges(true);
    };

    const handleSaveOrder = async () => {
        try {
            const payload = pscLocations.map((loc, index) => ({
                _id: loc._id,
                order: index + 1,
            }));

            const data = await reorderPSCLocations(payload);
            if (data.success) {
                console.log("Reorder saved");
                setHasChanges(false);
            } else {
                console.error("Error saving reorder:", data.message);
            }
        } catch (err) {
            console.error("Server error:", err);
        }
    };

    if (loading) {
        return (
            <Center mt={"20%"}>
                <Loading />
            </Center>
        );
    }
    if (error) {
        return <AccessDenied msg={error} />;
    }

    return (
        <Box maxW="full" mx="auto">
            <VStack spacing={6} align="stretch">
                <TitleHeader
                    title="PSC Locations"
                    buttonText={hasPermission('createPSCLocations') ? "New PSC Location" : null}
                    onClick={hasPermission('createPSCLocations') ? () => setIsOpenCreate(true) : null}
                    secondButton={hasPermission('exportPSCLocations')}
                    secondButtonOnClick={() => setExportModal(true)}
                    secondButtonText='Export'
                />

                <ExportPSCModal
                    isOpen={exportModal}
                    onClose={() => setExportModal(false)}
                    pscData={pscLocations}
                />

                {hasChanges && (
                    <Alert
                        status="info"
                        variant="subtle"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        textAlign="center"
                        height="140px"
                        borderRadius={20}
                    >
                        <AlertTitle mb={1} fontSize="lg">
                            PSC's order changed
                        </AlertTitle>
                        <Text>You have to save in order for these changes to apply</Text>
                        <AlertDescription>
                            <Button
                                isLoading={false}
                                loadingText={"Saving Order"}
                                mt={4}
                                size="sm"
                                colorScheme="red"
                                onClick={handleSaveOrder}
                            >
                                Save Order
                            </Button>
                        </AlertDescription>
                    </Alert>
                )}

                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="pscLocations">
                        {(provided) => (
                            <Table size="sm" variant="simple" {...provided.droppableProps} ref={provided.innerRef}>
                                <Thead bg={coolors.theadBg} color={coolors.textColor}>
                                    <Tr>
                                        <Th>#</Th>
                                        <Th>Location Name</Th>
                                        <Th>Address</Th>
                                        <Th>Hours of Operation</Th>
                                        <Th>Actions</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {pscLocations.map((location, index) => (
                                        <Draggable key={location._id} draggableId={location._id} index={index}>
                                            {(provided, snapshot) => (
                                                <Tr
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    borderBottom={`1px solid ${coolors.borderColor}`}
                                                    bg={snapshot.isDragging
                                                        ? "blue.100"
                                                        : index % 2 === 0
                                                            ? coolors.rowBgEven
                                                            : coolors.rowBgOdd}
                                                    color={coolors.textColor}
                                                    _hover={{ bg: coolors.rowHoverBg, color: "white" }}
                                                >
                                                    <Td>{index + 1}</Td>
                                                    <Td>{location.name}</Td>
                                                    <Td>{location.address}</Td>
                                                    <Td>{location.hoursOfOperation}</Td>
                                                    <Td>
                                                        <HStack spacing={3}>
                                                            {hasPermission("updatePSCLocations") && (
                                                                <Button
                                                                    size="xs"
                                                                    onClick={() => {
                                                                        setSelectedItem(location);
                                                                        setIsOpenEdit(true);
                                                                    }}
                                                                    colorScheme="green"
                                                                >
                                                                    <EditIcon mr={2} /> Edit
                                                                </Button>
                                                            )}
                                                            {hasPermission("deletePSCLocations") && (
                                                                <Button
                                                                    size="xs"
                                                                    onClick={() => {
                                                                        setSelectedItem(location);
                                                                        setIsOpenDelete(true);
                                                                    }}
                                                                    colorScheme="red"
                                                                >
                                                                    <DeleteIcon mr={2} /> Delete
                                                                </Button>
                                                            )}
                                                        </HStack>
                                                    </Td>
                                                </Tr>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </Tbody>
                            </Table>
                        )}
                    </Droppable>
                </DragDropContext>

                {/* Pagination stays the same */}
                <HStack mt={4} justify="center">
                    <Button onClick={() => fetchLocations(currentPage - 1)} isDisabled={currentPage == 1}>
                        « Previous
                    </Button>
                    {Array.from({ length: totalPages }).map((_, index) => (
                        <Button
                            key={index}
                            onClick={() => fetchLocations(index + 1)}
                            colorScheme={currentPage == index + 1 ? 'teal' : 'gray'}
                        >
                            {index + 1}
                        </Button>
                    ))}
                    <Button onClick={() => fetchLocations(currentPage + 1)} isDisabled={currentPage == totalPages}>
                        Next »
                    </Button>
                </HStack>
            </VStack>

            {/* Modals */}
            <CreatePSCLocationModal
                isOpen={isOpenCreate}
                onClose={() => setIsOpenCreate(false)}
                setPSCLocations={setPSCLocations}
            />
            <DeletePSCModal
                isOpen={isOpenDelete}
                onClose={() => setIsOpenDelete(false)}
                onDelete={(locationId) =>
                    setPSCLocations((prev) =>
                        prev.filter((location) => location._id !== locationId)
                    )
                }
                location={selectedItem}
            />
            {selectedItem && (
                <EditPSCLocationModal
                    isOpen={isOpenEdit}
                    onClose={() => setIsOpenEdit(false)}
                    locationData={selectedItem}
                    onSuccess={(data) =>
                        setPSCLocations((prev) =>
                            prev.map((location) =>
                                location._id === data._id ? { ...location, ...data } : location
                            )
                        )
                    }
                />
            )}
        </Box>
    );
};

export default PSCLocationsPage;
